/**
 * Error State Component
 *
 * Displays error messages with optional retry and dismiss actions.
 * Supports multiple variants for different contexts.
 */

import React from 'react';
import { AlertTriangle, RefreshCw, XCircle } from 'lucide-react';

interface ErrorStateProps {
  title: string;
  message?: string;
  onRetry?: () => void;
  onDismiss?: () => void;
  variant?: 'default' | 'inline' | 'compact';
  icon?: 'warning' | 'error';
}

export const ErrorState: React.FC<ErrorStateProps> = ({
  title,
  message,
  onRetry,
  onDismiss,
  variant = 'default',
  icon = 'warning'
}) => {
  const IconComponent = icon === 'warning' ? AlertTriangle : XCircle;

  if (variant === 'compact') {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-sm)',
        padding: 'var(--space-md)',
        backgroundColor: 'var(--color-red-50)',
        borderRadius: '4px',
        border: '1px solid var(--color-red-200)',
        fontSize: '0.875rem',
        color: 'var(--color-red-700)'
      }}>
        <IconComponent size={16} />
        <span>{title}</span>
        {onRetry && (
          <button
            onClick={onRetry}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              color: 'var(--color-red-700)',
              padding: 0
            }}
            title="Retry"
          >
            <RefreshCw size={14} />
          </button>
        )}
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <div
        role="alert"
        style={{
          display: 'flex',
          gap: 'var(--space-md)',
          padding: 'var(--space-lg)',
          backgroundColor: 'var(--color-red-50)',
          borderRadius: '6px',
          border: '1px solid var(--color-red-200)',
          color: 'var(--color-red-700)'
        }}
      >
        <IconComponent size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
        <div style={{ flex: 1 }}>
          <p style={{
            fontWeight: 600,
            marginBottom: message ? 'var(--space-xs)' : 0,
            margin: 0
          }}>
            {title}
          </p>
          {message && (
            <p style={{
              fontSize: '0.875rem',
              color: 'var(--color-red-600)',
              margin: 0
            }}>
              {message}
            </p>
          )}
        </div>
        {onRetry && (
          <button
            onClick={onRetry}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              color: 'var(--color-red-700)',
              padding: 0,
              flexShrink: 0
            }}
            title="Retry"
          >
            <RefreshCw size={18} />
          </button>
        )}
      </div>
    );
  }

  // Default variant
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'var(--space-3xl)',
      textAlign: 'center',
      backgroundColor: 'var(--color-red-50)',
      borderRadius: '8px',
      minHeight: '300px'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '64px',
        height: '64px',
        borderRadius: '50%',
        backgroundColor: 'var(--color-red-100)',
        marginBottom: 'var(--space-lg)'
      }}>
        <IconComponent size={36} color="var(--color-red-700)" />
      </div>
      <h3 style={{
        fontSize: '1.25rem',
        fontWeight: 600,
        color: 'var(--color-red-900)',
        marginBottom: 'var(--space-md)',
        margin: 0
      }}>
        {title}
      </h3>
      {message && (
        <p style={{
          fontSize: '0.875rem',
          color: 'var(--color-red-700)',
          maxWidth: '500px',
          lineHeight: 1.5,
          margin: 0,
          marginTop: 'var(--space-md)',
          marginBottom: 'var(--space-xl)'
        }}>
          {message}
        </p>
      )}
      <div style={{
        display: 'flex',
        gap: 'var(--space-md)',
        justifyContent: 'center'
      }}>
        {onRetry && (
          <button
            onClick={onRetry}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-sm)',
              padding: '10px 16px',
              backgroundColor: 'var(--color-red-700)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 500,
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--color-red-800)';
            }}
            onMouseOut={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--color-red-700)';
            }}
          >
            <RefreshCw size={16} />
            Try Again
          </button>
        )}
        {onDismiss && (
          <button
            onClick={onDismiss}
            style={{
              padding: '10px 16px',
              backgroundColor: 'var(--color-gray-200)',
              color: 'var(--color-gray-700)',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 500,
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--color-gray-300)';
            }}
            onMouseOut={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--color-gray-200)';
            }}
          >
            Dismiss
          </button>
        )}
      </div>
    </div>
  );
};

export default ErrorState;
